import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import manualAPI, { BulkManualCollectionResponse, ManualCollection } from '../services/manualCollectionService';
import clientStoreService from '../services/clientStoreService';
import vehicleService from '../services/vehicleService';
import riderService from '../services/riderService';
import deficitDetailsService from '../services/deficitDetailsService';

type DeficitAllocationPriority = 'RENT' | 'DAMAGE' | 'CHALLAN';
const PENDING_RIDERS_PER_PAGE = 10;

const formatCollectedDate = (value?: string | null) => {
  if (!value) return '-';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const calculateDeficitAllocation = (
  amount: number,
  summary: { totalRentAmount: number; channelAmount: number; damageCost: number } | null,
  priority: DeficitAllocationPriority = 'RENT',
) => {
  if (!summary || !Number.isFinite(amount) || amount <= 0) {
    return { rentAmount: 0, damageAmount: 0, channelAmount: 0, totalAvailable: 0 };
  }

  const available = {
    RENT: summary.totalRentAmount,
    DAMAGE: summary.damageCost,
    CHALLAN: summary.channelAmount,
  };
  const allocated: Record<DeficitAllocationPriority, number> = {
    RENT: 0,
    DAMAGE: 0,
    CHALLAN: 0,
  };
  const allocationOrder = [
    priority,
    ...(['RENT', 'DAMAGE', 'CHALLAN'] as DeficitAllocationPriority[]).filter(
      (category) => category !== priority,
    ),
  ];
  let remainingAmount = amount;

  allocationOrder.forEach((category) => {
    allocated[category] = Math.min(remainingAmount, available[category]);
    remainingAmount -= allocated[category];
  });

  return {
    rentAmount: allocated.RENT,
    damageAmount: allocated.DAMAGE,
    channelAmount: allocated.CHALLAN,
    totalAvailable: summary.totalRentAmount + summary.damageCost + summary.channelAmount,
  };
};

const ManualCollections: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  // Filters + pagination
  const [filters, setFilters] = useState<any>({ riderPublicId: '', clientName: '', city: '', fromDate: '', toDate: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [total, setTotal] = useState(0);
  const [data, setData] = useState<ManualCollection[]>([]);

  // Options
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [cityOptions, setCityOptions] = useState<any[]>([]);

  // Pending riders
  const [pendingRiders, setPendingRiders] = useState<any[]>([]);
  const [pendingTotal, setPendingTotal] = useState<number>(0);
  const [pendingQuery, setPendingQuery] = useState<string>('');
  const [pendingPage, setPendingPage] = useState(0);

  // Analytics state
  const [monthlyData, setMonthlyData] = useState<Array<{month:string; totalAmount:number; count:number}>>([]);
  const [cityData, setCityData] = useState<Array<{city:string; totalAmount:number; count:number}>>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFrom, setAnalyticsFrom] = useState('');
  const [analyticsTo, setAnalyticsTo] = useState('');
  const [prefetching, setPrefetching] = useState(false);
  const [exportingPending, setExportingPending] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMode, setExportMode] = useState<'all' | 'city' | 'date'>('all');
  const [exportCity, setExportCity] = useState('');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkManualCollectionResponse | null>(null);

  // Derived filtered list for search
  const filteredPendingRiders = (pendingRiders || []).filter((r: any) => {
    if (!pendingQuery) return true;
    const q = pendingQuery.toString().toLowerCase();
    return [
      r.riderPublicRiderId,
      r.riderName,
      r.phone,
      r.city,
      r.referredById,
    ].some((value) => (value || '').toString().toLowerCase().includes(q));
  });
  const paginatedPendingRiders = filteredPendingRiders.slice(
    pendingPage * PENDING_RIDERS_PER_PAGE,
    (pendingPage + 1) * PENDING_RIDERS_PER_PAGE,
  );

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create'|'edit'>('create');
  const [current, setCurrent] = useState<any>({});
  const [allocationPriority, setAllocationPriority] = useState<DeficitAllocationPriority>('RENT');
  const [selectedCollection, setSelectedCollection] = useState<ManualCollection | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [deficitSummary, setDeficitSummary] = useState<{ totalRentAmount: number; channelAmount: number; damageCost: number } | null>(null);
  const [deficitSummaryLoading, setDeficitSummaryLoading] = useState(false);

  useEffect(() => {
    if (tab === 0) fetchList();
    else fetchPendingRiders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, rowsPerPage]);

  useEffect(() => {
    const state = (location.state as any) || {};
    if (state?.pendingTab === 1) {
      setTab(1);
      const riderPublicId = state?.riderPublicId || '';
      if (riderPublicId) {
        setPendingQuery(riderPublicId);
        setTimeout(() => {
          void openCreateDialog({ riderPublicId, pending: state?.pending ?? 0 });
        }, 150);
      }
    }
  }, [location.state]);

  // Load dropdown options (clients, cities)
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const clientsRes = await clientStoreService.getClients({ page: 1, limit: 1000 });
        if (clientsRes && clientsRes.data) {
          // Deduplicate clients by name
          const seen = new Set<string>()
          const uniqueClients = clientsRes.data.filter((client) => {
            const key = client.name || client.clientCode || client.id
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
          setClientOptions(uniqueClients)
        }

        // Prefer cities from Vehicle Service (authoritative city service)
        try {
          const vehicleCitiesRes = await vehicleService.getCities({ isActive: true, isOperational: true });
          if (vehicleCitiesRes && vehicleCitiesRes.data && vehicleCitiesRes.data.length > 0) {
            // Normalize to { name, state }
            const normalized = vehicleCitiesRes.data.map((c: any) => ({ name: c.name, state: c.state }));
            setCityOptions(normalized);
            return; // done
          }
        } catch (vsErr) {
          console.warn('Vehicle service cities failed, falling back to client store cities', vsErr);
        }

        // Fallback to client store cities if vehicle service didn't return anything
        const citiesRes = await clientStoreService.getCities();
        if (citiesRes && citiesRes.data) setCityOptions(citiesRes.data);
      } catch (err) {
        console.warn('Failed to load client/city options', err);
      }
    };

    loadOptions();
  }, []);

  const fetchList = async (pageOverride?: number) => {
    setLoading(true);
    try {
      const usePage = pageOverride !== undefined ? pageOverride : page;
      const params: any = { page: usePage + 1, limit: rowsPerPage };
      if (filters.riderPublicId) params.riderPublicId = filters.riderPublicId;
      if (filters.clientName) params.clientName = filters.clientName;
      if (filters.city) params.city = filters.city;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const res = await manualAPI.list(params);
      // res should be of form { data: ManualCollection[], meta: { total, page, ... } }
      setData(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err: any) {
      console.error('Fetch manual collections failed', err?.response?.data || err.message || err);
      alert(err?.response?.data?.message || 'Failed to fetch manual collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRiders = async () => {
    setLoading(true);
    try {
      const res = await manualAPI.getPendingRiders();
      setPendingRiders(res.data || []);
      setPendingPage(0);

      const totalRes = await manualAPI.getTotalPendingAmount();
      setPendingTotal(Number(totalRes.pendingAmount || 0));
    } catch (err) {
      console.error('Fetch pending riders failed', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const params: any = {};
      if (analyticsFrom) params.fromDate = analyticsFrom;
      if (analyticsTo) params.toDate = analyticsTo;
      const [monthlyRes, cityRes] = await Promise.all([
        manualAPI.getMonthlyAnalytics(params),
        manualAPI.getCityAnalytics(params),
      ]);
      setMonthlyData(monthlyRes.data || []);
      setCityData(cityRes.data || []);
    } catch (err: any) {
      console.error('Failed to load analytics', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const exportPendingRiders = async () => {
    setExportingPending(true);
    try {
      const res = await manualAPI.exportPendingRiders();
      const rawContentType = res.headers?.['content-type'] || res.headers?.['Content-Type'];
      const contentType = typeof rawContentType === 'string'
        ? rawContentType
        : Array.isArray(rawContentType)
        ? rawContentType[0]
        : '';

      if (contentType.includes('application/json') || contentType.includes('text/')) {
        throw new Error('Export response was not a valid Excel file');
      }
      const blob = new Blob([res.data], { type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const rawContentDisposition = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      const contentDisposition = typeof rawContentDisposition === 'string'
        ? rawContentDisposition
        : Array.isArray(rawContentDisposition)
        ? rawContentDisposition[0]
        : '';
      const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(contentDisposition);
      const filename = filenameMatch?.[1] || `pending_riders_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, filename);
    } catch (err: any) {
      console.error('Export pending riders failed', err?.response?.data || err?.message || err);
      alert(err?.response?.data?.message || err?.message || 'Failed to export pending riders');
    } finally {
      setExportingPending(false);
    }
  };

  const exportAllCollections = async () => {
    setExportingAll(true);
    try {
      const params = exportMode === 'city'
        ? { city: exportCity }
        : exportMode === 'date'
        ? { fromDate: exportFromDate, toDate: exportToDate }
        : undefined;
      const res = await manualAPI.exportAll(params);
      const rawContentType = res.headers?.['content-type'] || res.headers?.['Content-Type'];
      const contentType = typeof rawContentType === 'string'
        ? rawContentType
        : Array.isArray(rawContentType)
        ? rawContentType[0]
        : '';

      if (contentType.includes('application/json') || contentType.includes('text/')) {
        throw new Error('Export response was not a valid Excel file');
      }

      const blob = new Blob([res.data], {
        type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const rawContentDisposition = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      const contentDisposition = typeof rawContentDisposition === 'string'
        ? rawContentDisposition
        : Array.isArray(rawContentDisposition)
        ? rawContentDisposition[0]
        : '';
      const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(contentDisposition);
      const filename = filenameMatch?.[1] || `all_manual_collections_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, filename);
      setExportDialogOpen(false);
    } catch (err: any) {
      console.error('Export all manual collections failed', err?.response?.data || err?.message || err);
      alert(err?.response?.data?.message || err?.message || 'Failed to export all collections');
    } finally {
      setExportingAll(false);
    }
  };

  const downloadBulkTemplate = () => {
    const workbook = XLSX.utils.book_new();
    const dataSheet = XLSX.utils.aoa_to_sheet([[
      'riderPublicId',
      'paymentMode',
      'amountPaid',
      'collectedDate',
      'utrNumber',
      'allocationPriority',
      'remarks',
    ]]);
    dataSheet['!cols'] = [
      { wch: 22 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 24 },
      { wch: 22 },
      { wch: 36 },
    ];
    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Manual Collections');
    const instructions = XLSX.utils.aoa_to_sheet([
      ['Column', 'Required', 'Instructions'],
      ['riderPublicId', 'Yes', 'Rider public ID (not the database ID).'],
      ['paymentMode', 'Yes', 'Use ONLINE or OFFLINE. ONLINE payments require a unique UTR number.'],
      ['amountPaid', 'Yes', 'Positive amount; cannot exceed the rider pending balance.'],
      ['collectedDate', 'Yes', 'Use YYYY-MM-DD.'],
      ['utrNumber', 'Conditional', 'Required for ONLINE payments; must be unique. Optional for OFFLINE payments.'],
      ['allocationPriority', 'No', 'Use RENT, DAMAGE, or CHALLAN. Defaults to RENT when blank.'],
      ['remarks', 'No', 'Optional collection remarks.'],
      [],
      ['Upload notes', '', 'Upload only the first sheet in this workbook after filling the Manual Collections sheet.'],
      ['Limits', '', 'Maximum 500 data rows per upload; use the provided column headers without changing them.'],
      ['Processing', '', 'Valid rows are saved individually. Failed rows include the spreadsheet row number and reason.'],
    ]);
    instructions['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
    const bytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      'manual_collections_template.xlsx',
    );
  };

  const uploadBulkCollections = async () => {
    if (!bulkFile) {
      setBulkError('Select an .xlsx file to upload.');
      return;
    }
    if (!bulkFile.name.toLowerCase().endsWith('.xlsx')) {
      setBulkError('Only .xlsx files are supported. Download and use the provided template.');
      return;
    }

    setBulkUploading(true);
    setBulkError(null);
    setBulkResult(null);
    try {
      const result = await manualAPI.bulkCreate(bulkFile);
      setBulkResult(result);
      if (tab === 0) {
        setPage(0);
        await fetchList(0);
      } else if (tab === 1) {
        await fetchPendingRiders();
      }
    } catch (err: any) {
      console.error('Bulk manual collection upload failed', err?.response?.data || err);
      setBulkError(err?.response?.data?.message || err?.message || 'Failed to upload manual collections.');
    } finally {
      setBulkUploading(false);
    }
  };

  const exportFilterValid = exportMode === 'all'
    || (exportMode === 'city' && Boolean(exportCity))
    || (exportMode === 'date' && Boolean(exportFromDate || exportToDate));

  useEffect(() => {
    if (tab === 2) loadAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, analyticsFrom, analyticsTo]);

  const loadDeficitSummary = async (riderPublicId?: string) => {
    const trimmedId = riderPublicId?.trim();

    if (!trimmedId) {
      setDeficitSummary(null);
      return;
    }

    try {
      setDeficitSummaryLoading(true);
      const response = await deficitDetailsService.getDeficitDetails({ publicRiderId: trimmedId });
      const payload = response?.data;

      if (response?.success && payload) {
        const summary = Array.isArray(payload)
          ? payload[0]
          : payload;

        setDeficitSummary({
          totalRentAmount: Number(summary?.totalRentAmount ?? 0),
          channelAmount: Number(summary?.channelAmount ?? 0),
          damageCost: Number(summary?.damageCost ?? 0),
        });
        return;
      }

      setDeficitSummary(null);
    } catch (err) {
      console.warn('Failed to fetch rider deficit summary', err);
      setDeficitSummary(null);
    } finally {
      setDeficitSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (openDialog && dialogMode === 'create') {
      void loadDeficitSummary(current?.riderPublicId);
    } else {
      setDeficitSummary(null);
    }
  }, [openDialog, dialogMode, current?.riderPublicId]);

  const openCreateDialog = async (prefill?: any) => {
    setDialogMode('create');
    setDeficitSummary(null);
    setAllocationPriority('RENT');
    // Basic defaults + any provided prefill
    setCurrent({ collectedDate: '', amountPaid: '', paymentMode: '', remarks: '', utrNumber: '', riderName: '', riderMobile: '', vehicleNumber: '', purpose: '', allocationPriority: 'RENT', ...prefill });
    setOpenDialog(true);

    // If called with a riderPublicId, try to fetch the rider profile and prefill known fields
    if (prefill && prefill.riderPublicId) {
      try {
        setPrefetching(true);
        const resp = await riderService.getRiderByPublicId(prefill.riderPublicId);
        if (resp && resp.data) {
          const r = resp.data as any;
          setCurrent((prev: any) => ({
            ...prev,
            riderName: r.name || prev.riderName,
            riderMobile: r.phone || prev.riderMobile,
            city: r.city || prev.city,
            clientRiderId: r.clientRiderId || prev.clientRiderId,
            // If pending amount was provided in prefill, set as default amountPaid
            amountPaid: prefill.pending !== undefined ? prefill.pending : prev.amountPaid,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch rider profile for prefill', err);
      } finally {
        setPrefetching(false);
      }
    }
  };

  const openEditDialog = (row: any) => {
    setSelectedCollection(null);
    setDialogMode('edit');
    setCurrent({ ...row, collectedDate: row.collectedDate ? row.collectedDate.split('T')[0] : '' });
    setOpenDialog(true);
  };

  const openCollectionDetails = (row: ManualCollection) => {
    setSelectedCollection(row);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (dialogMode === 'create') {
        await manualAPI.create({ ...current, allocationPriority });
      } else {
        await manualAPI.update(current.id, current);
      }

      setOpenDialog(false);
      setConfirmationOpen(false);

      const state = (location.state as any) || {};
      if (dialogMode === 'create' && state.returnToRiderProfile && state.riderProfileId) {
        navigate(`/rider-management/${state.riderProfileId}`, { state: { tab: 8 } });
        return;
      }

      // Refresh view depending on active tab
      if (tab === 0) fetchList(); else fetchPendingRiders();
    } catch (err: any) {
      console.error('Submit manual collection failed', err?.response?.data || err);
      alert(err?.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const requestSubmitConfirmation = () => {
    if (dialogMode === 'create' && !canSaveCollection) return;
    setConfirmationOpen(true);
  };

  const paymentAmount = Number(current.amountPaid || 0);
  const allocation = calculateDeficitAllocation(paymentAmount, deficitSummary, allocationPriority);
  const canSaveCollection = Boolean(
    current.riderPublicId &&
    paymentAmount > 0 &&
    current.paymentMode &&
    current.collectedDate &&
    deficitSummary &&
    paymentAmount <= allocation.totalAvailable,
  );

  return (
  <Box sx={{ pt: 2 }}>
    <Typography
      variant="h5"
      sx={{
        mb: 2,
        mt: 1,
        fontWeight: 600,
      }}
    >
      Manual Collections
    </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="All Collections" />
          <Tab label="Pending Riders" />
          <Tab label="Analytics" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Rider Public ID" value={filters.riderPublicId} onChange={(e) => setFilters({ ...filters, riderPublicId: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="filter-client-name-label">Client Name</InputLabel>
                  <Select
                    labelId="filter-client-name-label"
                    value={filters.clientName || ''}
                    label="Client Name"
                    onChange={(e: any) => {
                      const val = e.target.value
                      setFilters({ ...filters, clientName: val })
                      if (val === '') {
                        setPage(0)
                        fetchList(0)
                      }
                    }}
                  >
                    <MenuItem value=""><em>All</em></MenuItem>
                    {clientOptions.map((c) => (
                      <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="filter-city-label">City</InputLabel>
                  <Select
                    labelId="filter-city-label"
                    value={filters.city || ''}
                    label="City"
                    onChange={(e: any) => {
                      const val = e.target.value
                      setFilters({ ...filters, city: val })
                      if (val === '') {
                        setPage(0)
                        fetchList(0)
                      }
                    }}
                  >
                    <MenuItem value=""><em>All</em></MenuItem>
                    {cityOptions.map((c: any) => (
                      <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button variant="contained" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => { setPage(0); fetchList(); }}>Search</Button>
                  <Button sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => { setFilters({ riderPublicId: '', clientName: '', city: '', fromDate: '', toDate: '' }); setPage(0); fetchList(0); }}>Reset</Button>
                  <Button variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => openCreateDialog()}>Create</Button>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    onClick={() => {
                      setBulkFile(null);
                      setBulkError(null);
                      setBulkResult(null);
                      setBulkDialogOpen(true);
                    }}
                  >
                    Bulk Upload
                  </Button>
                  <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => setExportDialogOpen(true)} disabled={loading || exportingAll}>
                    Download All
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              {loading ? <CircularProgress /> : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Public Rider ID</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>Rider Mobile</TableCell>
                          <TableCell>Client Name</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Collected Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.map((row) => (
                          <TableRow
                            key={row.id}
                            hover
                            tabIndex={0}
                            onClick={() => openCollectionDetails(row)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') openCollectionDetails(row);
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{row.riderPublicId}</TableCell>
                            <TableCell>{row.city}</TableCell>
                            <TableCell>{row.riderMobile || '-'}</TableCell>
                            <TableCell>{row.clientName || '-'}</TableCell>
                            <TableCell align="right">{Number(row.amountPaid || 0).toLocaleString()}</TableCell>
                            <TableCell>{formatCollectedDate(row.collectedDate)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }} />
                </>
              )}
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ mt: 2 }}>
            {loading ? <CircularProgress /> : (
              <>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Total Pending Amount</Typography>
                    <Box sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', px: 2, py: 0.75, borderRadius: 1, fontWeight: 700, fontSize: '1rem' }}>
                      {Number(pendingTotal || 0).toLocaleString()}
                    </Box>
                    <Box sx={{ px: 1, py: 0.5, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.06)', fontWeight: 600 }}>
                      {pendingRiders.length || 0} riders
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <TextField size="small" placeholder="Search rider, ID, phone, city, or referrer" value={pendingQuery} onChange={(e) => { setPendingQuery(e.target.value); setPendingPage(0); }} sx={{ width: { xs: '100%', sm: 280 } }} />
                    <IconButton color="primary" onClick={() => fetchPendingRiders()} title="Refresh pending riders">
                      <RefreshIcon />
                    </IconButton>
                    <Button variant="outlined" fullWidth={false} sx={{ width: { xs: '100%', sm: 'auto' } }} startIcon={<DownloadIcon />} onClick={exportPendingRiders} disabled={loading || exportingPending}>
                      {exportingPending ? 'Exporting...' : 'Download XLSX'}
                    </Button>
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
                  Showing {paginatedPendingRiders.length} of {filteredPendingRiders.length} matching riders
                </Typography>

                <TableContainer sx={{ mt: 1 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rider Name</TableCell>
                        <TableCell>Public Rider ID</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Referred By ID</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="right">Pending</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPendingRiders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center' }}>No riders found</TableCell>
                        </TableRow>
                      ) : (
                        paginatedPendingRiders.map((r) => (
                          <TableRow key={r.riderPublicRiderId}>
                            <TableCell>{r.riderName || '-'}</TableCell>
                            <TableCell>{r.riderPublicRiderId || '-'}</TableCell>
                            <TableCell>{r.city || '-'}</TableCell>
                            <TableCell>{r.phone || '-'}</TableCell>
                            <TableCell>{r.referredById || '-'}</TableCell>
                            <TableCell>{r.isActive == null ? '-' : r.isActive ? 'Yes' : 'No'}</TableCell>
                            <TableCell align="right">{Number(r.pending || 0).toLocaleString()}</TableCell>
                            <TableCell>
                              <Button size="small" onClick={() => openCreateDialog({ riderPublicId: r.riderPublicRiderId, pending: r.pending })}>Create Collection</Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredPendingRiders.length}
                  page={pendingPage}
                  onPageChange={(_, nextPage) => setPendingPage(nextPage)}
                  rowsPerPage={PENDING_RIDERS_PER_PAGE}
                  rowsPerPageOptions={[PENDING_RIDERS_PER_PAGE]}
                />
              </>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ mt: 2 }}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    label="From"
                    type="date"
                    value={analyticsFrom}
                    onChange={(e) => setAnalyticsFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="To"
                    type="date"
                    value={analyticsTo}
                    onChange={(e) => setAnalyticsTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <Button variant="contained" onClick={() => loadAnalytics()} startIcon={<SearchIcon />}>Fetch</Button>
                </Stack>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6">Monthly Manual Collections</Typography>
                    {analyticsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyData.map(m => ({ month: m.month, total: m.totalAmount }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="total" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}

                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Month</TableCell><TableCell>Amount</TableCell><TableCell>Count</TableCell></TableRow></TableHead>
                        <TableBody>
                          {monthlyData.map(m => (
                            <TableRow key={m.month}><TableCell>{m.month}</TableCell><TableCell>{m.totalAmount}</TableCell><TableCell>{m.count}</TableCell></TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6">City-wise Manual Collections</Typography>
                    {analyticsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={cityData.map(c => ({ city: c.city, total: c.totalAmount }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="city" />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="total" fill="#82ca9d" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}

                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>City</TableCell><TableCell>Amount</TableCell><TableCell>Count</TableCell></TableRow></TableHead>
                        <TableBody>
                          {cityData.map(c => (
                            <TableRow key={c.city}><TableCell>{c.city}</TableCell><TableCell>{c.totalAmount}</TableCell><TableCell>{c.count}</TableCell></TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {dialogMode === 'create' ? 'Create Manual Collection' : 'Edit Manual Collection'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Record the payment and update the rider's outstanding deficit.
              </Typography>
            </Box>
            {prefetching ? <CircularProgress size={20} /> : null}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ pt: { xs: 0.5, sm: 1 } }}>
            {openDialog && dialogMode === 'create' && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: 'rgba(0,0,0,0.01)' }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Deficit Details
                      </Typography>
                      {deficitSummaryLoading && <CircularProgress size={16} />}
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Allocation priority:</strong> The selected pending category is paid first. Any remaining payment follows the existing Rent, Damage, Challen order.
                    </Alert>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="allocation-priority-label">Apply payment to first</InputLabel>
                      <Select
                        labelId="allocation-priority-label"
                        label="Apply payment to first"
                        value={allocationPriority}
                        onChange={(event) =>
                          setAllocationPriority(event.target.value as DeficitAllocationPriority)
                        }
                      >
                        <MenuItem value="RENT">Rent Pending</MenuItem>
                        <MenuItem value="DAMAGE">Damage Pending</MenuItem>
                        <MenuItem value="CHALLAN">Challen Amount Pending</MenuItem>
                      </Select>
                    </FormControl>

                    {deficitSummary ? (
                      <>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 1.5, height: '100%', borderRadius: 1.5, backgroundColor: '#F3F6FF' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Rent Pending</Typography>
                              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 700 }}>{Number(deficitSummary.totalRentAmount || 0).toLocaleString()}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 1.5, height: '100%', borderRadius: 1.5, backgroundColor: '#FFF7ED' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Damage Pending</Typography>
                              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 700 }}>{Number(deficitSummary.damageCost || 0).toLocaleString()}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 1.5, height: '100%', borderRadius: 1.5, backgroundColor: '#F0FDF4' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Challen Amount Pending</Typography>
                              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 700 }}>{Number(deficitSummary.channelAmount || 0).toLocaleString()}</Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {paymentAmount > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              Payment Allocation Preview
                            </Typography>
                            <Grid container spacing={1.5}>
                              <Grid item xs={4}>
                                <Typography variant="caption">Rent Amount</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{allocation.rentAmount.toLocaleString()}</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption">Damage Amount</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{allocation.damageAmount.toLocaleString()}</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption">Challen Amount</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{allocation.channelAmount.toLocaleString()}</Typography>
                              </Grid>
                            </Grid>
                            {paymentAmount > allocation.totalAvailable && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                Amount Paid cannot exceed the available deficit balance.
                              </Alert>
                            )}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {current?.riderPublicId ? 'No deficit data found for this rider.' : 'Enter a rider public ID to load deficit details.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Rider Public ID" value={current.riderPublicId || ''} disabled helperText="Selected rider" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Amount Paid"
                type="number"
                value={current.amountPaid || ''}
                onChange={(e) => setCurrent({ ...current, amountPaid: Number(e.target.value) })}
                disabled={dialogMode === 'edit'}
                inputProps={{ min: 0, step: '0.01' }}
                helperText={dialogMode === 'edit' ? 'Amount cannot be changed after submission' : deficitSummary ? `Available balance: ${allocation.totalAvailable.toLocaleString()}` : 'Enter payment amount'}
                error={paymentAmount > allocation.totalAvailable && Boolean(deficitSummary)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel required id="payment-mode-label">Payment Mode</InputLabel>
                <Select labelId="payment-mode-label" value={current.paymentMode || ''} label="Payment Mode" onChange={(e: any) => setCurrent({ ...current, paymentMode: e.target.value })}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  <MenuItem value="ONLINE">ONLINE</MenuItem>
                  <MenuItem value="OFFLINE">OFFLINE</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="UTR Number" value={current.utrNumber || ''} onChange={(e) => setCurrent({ ...current, utrNumber: e.target.value })} helperText={current.paymentMode === 'ONLINE' ? 'Required for online payments' : 'Optional'} required={current.paymentMode === 'ONLINE'} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth required type="date" label="Collected Date" InputLabelProps={{ shrink: true }} value={current.collectedDate || ''} onChange={(e) => setCurrent({ ...current, collectedDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Remarks" value={current.remarks || ''} onChange={(e) => setCurrent({ ...current, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={requestSubmitConfirmation} disabled={submitting || (dialogMode === 'create' && !canSaveCollection)}>
            {submitting ? <CircularProgress size={18} /> : dialogMode === 'create' ? 'Save Collection' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedCollection)} onClose={() => setSelectedCollection(null)} fullWidth maxWidth="sm">
        <DialogTitle>Collection Details</DialogTitle>
        <DialogContent dividers>
          {selectedCollection && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              {[
                ['Public Rider ID', selectedCollection.riderPublicId],
                ['Client Rider ID', selectedCollection.clientRiderId],
                ['Rider Name', selectedCollection.riderName],
                ['Rider Mobile', selectedCollection.riderMobile],
                ['City', selectedCollection.city],
                ['Client Name', selectedCollection.clientName],
                ['Vehicle Number', selectedCollection.vehicleNumber],
                ['Amount Paid', selectedCollection.amountPaid != null ? Number(selectedCollection.amountPaid).toLocaleString() : null],
                ['Purpose', selectedCollection.purpose],
                ['Payment Mode', selectedCollection.paymentMode],
                ['UTR Number', selectedCollection.utrNumber],
                ['Collected By', selectedCollection.collectedBy],
                ['Collected Date', selectedCollection.collectedDate ? formatCollectedDate(selectedCollection.collectedDate) : null],
                ['Remarks', selectedCollection.remarks],
                ['Created At', selectedCollection.createdAt ? new Date(selectedCollection.createdAt).toLocaleString() : null],
                ['Updated At', selectedCollection.updatedAt ? new Date(selectedCollection.updatedAt).toLocaleString() : null],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.5, height: '100%', borderRadius: 1.5, backgroundColor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, wordBreak: 'break-word' }}>{value || '-'}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCollection(null)}>Close</Button>
          {selectedCollection && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => openEditDialog(selectedCollection)}>
              Edit Collection
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDialogOpen}
        onClose={() => !bulkUploading && setBulkDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Bulk Upload Manual Collections</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Download the template, fill in one collection per row, then upload the completed .xlsx file.
              Valid rows are processed individually, so failed rows do not undo successful collections.
            </Typography>
            <Alert severity="info">
              Required columns: riderPublicId, paymentMode (ONLINE/OFFLINE), amountPaid, and collectedDate (YYYY-MM-DD).
              ONLINE payments require a unique UTR number. Maximum 500 rows per upload.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadBulkTemplate}>
                Download Template
              </Button>
              <Button component="label" variant="contained" startIcon={<UploadFileIcon />} disabled={bulkUploading}>
                Choose Excel File
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setBulkFile(file);
                    setBulkError(null);
                    setBulkResult(null);
                    event.target.value = '';
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {bulkFile?.name || 'No file selected'}
              </Typography>
            </Box>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
            {bulkResult && (
              <>
                <Alert severity={bulkResult.summary.failureCount ? 'warning' : 'success'}>
                  Processed {bulkResult.summary.totalRows} rows: {bulkResult.summary.successCount} succeeded, {bulkResult.summary.failureCount} failed.
                </Alert>
                {bulkResult.failures.length > 0 && (
                  <Box sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Rows requiring correction</Typography>
                    <Stack spacing={0.5}>
                      {bulkResult.failures.map((failure) => (
                        <Typography key={`${failure.rowNumber}-${failure.riderPublicId}`} variant="body2">
                          Row {failure.rowNumber}{failure.riderPublicId ? ` (${failure.riderPublicId})` : ''}: {failure.message}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>Close</Button>
          <Button
            variant="contained"
            startIcon={bulkUploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
            onClick={uploadBulkCollections}
            disabled={bulkUploading || !bulkFile}
          >
            {bulkUploading ? 'Uploading...' : 'Upload Collections'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exportDialogOpen} onClose={() => !exportingAll && setExportDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Download Collections</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which collection records to include in the Excel file.
          </Typography>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="export-mode-label">Download scope</InputLabel>
              <Select
                labelId="export-mode-label"
                value={exportMode}
                label="Download scope"
                onChange={(event) => setExportMode(event.target.value as 'all' | 'city' | 'date')}
              >
                <MenuItem value="all">All data</MenuItem>
                <MenuItem value="city">Based on City</MenuItem>
                <MenuItem value="date">Based on Collected Date</MenuItem>
              </Select>
            </FormControl>

            {exportMode === 'city' && (
              <FormControl fullWidth required>
                <InputLabel id="export-city-label">City</InputLabel>
                <Select
                  labelId="export-city-label"
                  value={exportCity}
                  label="City"
                  onChange={(event) => setExportCity(event.target.value)}
                >
                  {cityOptions.map((city: any) => (
                    <MenuItem key={city.name} value={city.name}>{city.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {exportMode === 'date' && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Collected from"
                    value={exportFromDate}
                    onChange={(event) => setExportFromDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Collected to"
                    value={exportToDate}
                    onChange={(event) => setExportToDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {exportFromDate && exportToDate && exportFromDate > exportToDate && (
                  <Grid item xs={12}>
                    <Alert severity="error">The start date cannot be after the end date.</Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exportingAll}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={exportingAll ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={exportAllCollections}
            disabled={exportingAll || !exportFilterValid || (exportMode === 'date' && Boolean(exportFromDate && exportToDate && exportFromDate > exportToDate))}
          >
            {exportingAll ? 'Exporting...' : 'Download Excel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmationOpen} onClose={() => !submitting && setConfirmationOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Manual Collection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please confirm these payment details before saving. The deficit balances will be updated using the priority shown below.
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2"><strong>Rider:</strong> {current.riderPublicId || 'N/A'}</Typography>
            <Typography variant="body2"><strong>Amount paid:</strong> {paymentAmount.toLocaleString()}</Typography>
            <Typography variant="body2"><strong>Collected date:</strong> {current.collectedDate || 'N/A'}</Typography>
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            Rent Amount: {allocation.rentAmount.toLocaleString()} → Damage Amount: {allocation.damageAmount.toLocaleString()} → Channel Amount: {allocation.channelAmount.toLocaleString()}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : 'Confirm & Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualCollections;
